import { Divider } from '@/design-system/components/Divider';
import { Icon } from '@/design-system/components/icons/Icon';
import { UniInput } from '@/design-system/components/inputs';
import { UniTabHeader } from '@/design-system/components/tabs';
import { TextBody } from '@/design-system/components/typography';
import { useNavigation } from '@react-navigation/native';
import { ActivityIndicator, FlatList, Pressable, SxProp, View } from 'dripsy';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { RootStackRoutes } from 'types';
import { BillPaymentStackRoutes } from '@/features/bill-payment/payment.navigator';
import { HeaderBackgroundImageComponent } from '@/features/bill-payment/screens/components/HeaderBackgroundImageComponent';
import { UnionPayStackRoutes } from '@/features/bill-payment/screens/union-pay/type';
import { useTransferPeer } from '@/features/bill-payment/hooks/useUnionPay.hook';

export interface SelectAccountNativeScreenProps {
  onSelect?: (data: any) => void;
  onBack?: (flag?: boolean) => void;
}

export const SelectAccountNativeScreen: React.FC<SelectAccountNativeScreenProps> = (props) => {
  const { onSelect, onBack } = props;
  const navigation = useNavigation();
  const navigationAny = useNavigation<any>();
  const { t } = useTranslation('bill-payment-union-pay');

  const [search, setSearch] = useState<string | undefined>(undefined);
  const { transferPeers, isLoading } = useTransferPeer({ types: ['UNIONPAY'], current: 1, pageSize: 10, label: search });

  return (
    <View sx={sxStyles.container}>
      <View sx={{ ...sxStyles.header }}>
        <HeaderBackgroundImageComponent />
        <UniTabHeader
          title={t('unionPay::payment-instruction.title--select-account')}
          showBackButton={navigation.canGoBack()}
          onBack={() => {
            onBack && onBack();
          }}
          labelColor="$white"
          sx={{ ...sxStyles.tab, px: '$4', py: '$5' }}
        />
      </View>
      <View sx={sxStyles.body}>
        <View sx={sxStyles.card}>
          <View sx={sxStyles.table}>
            <View sx={sxStyles.operations}>
              <View sx={sxStyles.filters}>
                <UniInput
                  sx={sxStyles.filter_search}
                  size="default"
                  prefixIcon="search"
                  placeholder={t('unionPay::payment-instruction.text--select-account-account-name-no')}
                  onValueChange={(value) => setSearch(value)}
                />
              </View>
            </View>
            <FlatList
              data={transferPeers}
              renderItem={({ item }) => {
                return (
                  <Pressable
                    onPress={() => {
                      onSelect &&
                        onSelect({
                          ...item,
                          accountLabel:
                            item?.label || `${item?.attributes?.receiverFirstName || ''} ${item?.attributes?.receiverSurname || ''}`,
                          accountNumber: item?.attributes?.receiverAccountNumber
                        });
                      onBack && onBack();
                    }}>
                    <View sx={{ paddingY: '$3', paddingX: '$4', gap: '$1' }}>
                      <TextBody sx={{ color: '$textPrimary' }}>
                        {item?.label || `${item?.attributes?.receiverFirstName || ''} ${item?.attributes?.receiverSurname || ''}`}
                      </TextBody>
                      <TextBody sx={{ color: '$textPrimary' }}>{item?.attributes?.receiverAccountNumber}</TextBody>
                    </View>
                  </Pressable>
                );
              }}
            />
            {isLoading && (
              <View sx={{ padding: '$5' }}>
                <ActivityIndicator size="large" color={'$disable'} />
              </View>
            )}
          </View>
          <View sx={{ paddingX: '$4' }}>
            <Divider sx={{ margin: 'auto', backgroundColor: '$borderField', width: '100%' }} />
            <View sx={{ paddingY: '$6', gap: '$1' }}>
              <Pressable
                onPress={() => {
                  navigationAny.navigate(RootStackRoutes.BillPayment, {
                    screen: BillPaymentStackRoutes.UnionPay,
                    params: {
                      screen: UnionPayStackRoutes.AddNewAccount,
                      onAccountSubmit: (flag: boolean, data: any) => {
                        if (flag) {
                          onSelect && onSelect(data);
                          onBack && onBack();
                        }
                      }
                    }
                  });
                }}
                sx={{ flexDirection: 'row', gap: '$1', alignItems: 'center' }}>
                <Icon name="addAccount" width={20} height={20} />
                <TextBody sx={{ color: '$textHyperlink' }}>{t('unionPay::payment-instruction.title--add-new-account')}</TextBody>
              </Pressable>
            </View>
            <Divider sx={{ margin: 'auto', backgroundColor: '$borderField', width: '100%' }} />
          </View>
        </View>
      </View>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    backgroundColor: '$backgroundPrimary',
    width: '100%',
    flex: 1
  },
  header: {
    flexDirection: 'column'
  },
  tab: {
    backgroundColor: 'transparent',
    width: '98%'
  },
  card: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    gap: '$3',
    borderRadius: '$radius0',
    boxShadow: 'none',
    paddingY: '$5',
    backgroundColor: '$white'
  },
  table: {
    flexDirection: 'column',
    gap: '$3'
  },
  operations: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    px: '$4',
    zIndex: 1
  },
  filters: {
    flexDirection: 'row',
    gap: '$5',
    width: '100%'
  },
  filter_search: {
    width: '100%'
  }
};
